import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { samePhone } from '@/lib/phone';

// Janela de 24 horas do WhatsApp oficial: o estado e o template de alternativa.
// Separado dos componentes (WindowBar.tsx) para o fast refresh do Vite.

const DAY_MS = 24 * 60 * 60 * 1000;

export interface WindowState {
  /** A janela está aberta agora. */
  open: boolean;
  /** Milissegundos até fechar (0 quando fechada). */
  leftMs: number;
  /** O contato nunca escreveu. */
  never: boolean;
}

/** Estado da janela a partir da última mensagem do contato. Atualiza a cada minuto. */
export function useWindowState(lastInboundAt?: string | null): WindowState {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return useMemo(() => {
    if (!lastInboundAt) return { open: false, leftMs: 0, never: true };
    const left = new Date(lastInboundAt).getTime() + DAY_MS - now;
    return { open: left > 0, leftMs: Math.max(left, 0), never: false };
  }, [lastInboundAt, now]);
}

/** Template de alternativa do projeto (nome) — undefined quando não há. */
export function useFallbackName(enabled: boolean): string | undefined {
  const { data } = useQuery({
    queryKey: ['templates', 'fallback'],
    enabled,
    queryFn: async () => ((await apiFetch('GET', '/templates/fallback')).data as { template?: { name: string } | null })?.template ?? null,
  });
  return data?.name;
}


/** Janela de 24h com o destino de teste: estado + se dá para saber (telefone completo). */
export function useDestinationWindow(phone: string) {
  const digits = phone.replace(/\D/g, '');
  const known = digits.length >= 8;
  const { data: convs = [] } = useQuery({
    queryKey: ['conversations', ''],
    enabled: known,
    queryFn: async () => ((await apiFetch('GET', '/conversations')).data as { data?: { chat_jid: string; last_inbound_at?: string }[] })?.data ?? [],
  });
  const last = useMemo(
    () => convs.find((c) => c.chat_jid.endsWith('@s.whatsapp.net') && samePhone(c.chat_jid.split('@')[0], digits))?.last_inbound_at,
    [convs, digits],
  );
  return { known, ...useWindowState(last) };
}

